<template>
	<Layout>
		<JumbotronCarousel
			v-if="slides.length > 0"
			:slides="slides"
			:has-header-sponsors="headerSponsors && headerSponsors.length > 0"
			:alternative-header-active="$arenaConfig.alternativeHeader"
		>
			<template #after>
				<HeaderSponsors
					v-if="headerSponsors && !$arenaConfig.alternativeHeader"
					:sponsors="headerSponsors"
				/>
			</template>
		</JumbotronCarousel>
		<JumbotronKrone
			v-else-if="$arenaConfig.isKrone"
			:is-league="true"
			:title="title"
		>
			<template #after>
				<HeaderSponsors
					v-if="headerSponsors && !$arenaConfig.alternativeHeader"
					:sponsors="headerSponsors"
				/>
			</template>
		</JumbotronKrone>
		<Jumbotron
			v-else
			:bg-image-url="bgImageUrl"
			:main-image-url="mainImageUrl"
			:title="title"
			:special-announcement="false"
		>
			<template #after>
				<HeaderSponsors
					v-if="headerSponsors && !$arenaConfig.alternativeHeader"
					:sponsors="headerSponsors"
				/>
			</template>
		</Jumbotron>
		<HeaderSponsors
			v-if="headerSponsors && $arenaConfig.alternativeHeader"
			:sponsors="headerSponsors"
			:alternative-header-active="true"
		/>
		<FeedNavigationDesktop />
		<div class="l-container">
			<div
				v-for="lane in mixedLanes"
				:key="lane.id"
				class="l-content-section-w js-content-section"
				:class="[
					{
						'-highlighted': lane.highlighted
					},
					`-${lane.lane_type}`
				]"
			>
				<SectionChildNav
					v-if="teams.length && lane.lane_type === ArenaLaneType.NAVIGATION && lane.visible"
					type="team"
					:lane-config="lane.config"
					:items="teams"
				/>
				<SocialLane
					v-if="lane.lane_type === ArenaLaneType.STORIES && lane.visible && socialPosts.length > 0"
					:social-posts="socialPosts"
				/>
				<MomentsLane
					v-if="lane.lane_type === ArenaLaneType.MOMENTS && lane.visible"
					:items="lane.items"
				/>
				<EventLane
					v-if="lane.visible && lane.storeId"
					:store-id="lane.storeId"
					:album-id="lane.albumId"
					:album-icon="lane.albumIcon"
					:title="lane.title"
					:subtitle="lane.subtitle"
					:more-link="lane.moreLink"
					:lane-type="lane.lane_type"
					:highlighted="lane.highlighted"
					:ad="lane.ad"
				/>
				<AdLane
					v-if="lane.lane_type === ArenaLaneType.AD && lane.visible"
					:ad="lane.ad"
				/>
				<CollectionLane
					v-if="lane.lane_type === ArenaLaneType.COLLECTION && lane.visible"
					:collection="lane.arena_collection"
					:center-horizontally="lane.config?.centerHorizontally || false"
				/>
			</div>
			<EmptyState
				v-if="noEvents"
				:title="$I18n.trans_choice('commons.event', 2)"
				:text="$I18n.trans('arena.placeholder.replays')"
			/>
		</div>
	</Layout>
</template>

<script>
import Layout from '@js/Views/Layout';
import Jumbotron from '@js/components/Jumbotron';
import JumbotronKrone from '@js/components/JumbotronKrone';
import JumbotronCarousel from '@js/components/JumbotronCarousel';
import SectionChildNav from '@js/components/SectionChildNav';
import EventLane from '@js/components/EventLane';
import SocialLane from '@js/components/SocialLane';
import AdLane from '@js/components/AdLane';
import CollectionLane from '@js/components/CollectionLane';
import EmptyState from '@js/components/EmptyState';
import FeedNavigationDesktop from '@js/components/FeedNavigationDesktop';
import HeaderSponsors from '@js/components/HeaderSponsors';
import MomentsLane from '@js/components/MomentsLane';
import { resolveReplayEventsMoreLink, resolveUpcomingEventsMoreLink } from '@js/utils/arenaCenterLinks';
import { laneMixer } from '@js/utils/laneMixer';
import { ArenaLaneType } from '@shared/enums/ArenaLaneType';
import { AdType } from '@shared/enums/AdType';
import laroute from '@laroute';

export default {
	name:       'ViewLeague',
	components: {
		Layout,
		Jumbotron,
		JumbotronKrone,
		JumbotronCarousel,
		SectionChildNav,
		EventLane,
		EmptyState,
		SocialLane,
		AdLane,
		CollectionLane,
		FeedNavigationDesktop,
		HeaderSponsors,
		MomentsLane,
	},
	props: {
		bgImageUrl: {
			type:    String,
			default: null,
		},
		mainImageUrl: {
			type:    String,
			default: null,
		},
		title: {
			type:    String,
			default: null,
		},
		slides: {
			type:    Array,
			default: () => [],
		},
		enableLiveheader: {
			type:    Boolean,
			default: false,
		},
		teams: {
			type:    Array,
			default: () => [],
		},
		lanes: {
			type:    Array,
			default: () => [],
		},
		socialPosts: {
			type:    Array,
			default: () => [],
		},
		ads: {
			type:    Array,
			default: () => [],
		},
		highlightedAd: {
			type:    Object,
			default: null,
		},
		momentsLaneEnabled: {
			type:    Boolean,
			default: false,
		},
		momentsLaneItems: {
			type:    Array,
			default: () => [],
		},
	},
	data() {
		return {
			ArenaLaneType,
		};
	},
	computed: {
		noEvents() {
			return this.mixedLanes.filter(lane => !!lane.storeId).every(lane => {
				return (this.$store.state.events[lane.storeId]?.items || []).length === 0;
			});
		},
		eventCenterBaseUrl() {
			return this.$arenaConfig.isCustomDomain
				? laroute.route('arena.event.center.custom')
				: laroute.route('arena.event.center', { slug: this.$arenaConfig.slug, league: this.$arenaConfig.id });
		},
		videoCenterBaseUrl() {
			return this.$arenaConfig.isCustomDomain || this.$arenaConfig.isKrone
				? laroute.route('arena.video.center.custom')
				: laroute.route('arena.video.center', { slug: this.$arenaConfig.slug, league: this.$arenaConfig.id });
		},
		mixedLanes() {
			// get all albums from store
			const albumEntries = Object.entries(this.$store.state.events).filter(([key]) => key.startsWith('album_'));
			const albums = albumEntries.map(([key, value]) => {
				return {
					id:       parseInt(key.replace('album_', '')),
					events:   value.items,
					subtitle: value.subtitle,
					imgUrl:   value.imgUrl,
				};
			});
			return laneMixer(
				albums,
				this.lanes,
				this.$store,
				{
					highlighted: this.highlightedAd,
					normal:      this.ads,
				},
				{
					moreLinks: {
						upcoming: resolveUpcomingEventsMoreLink({
							eventCenterBaseUrl:             this.eventCenterBaseUrl,
							videoCenterBaseUrl:             this.videoCenterBaseUrl,
							eventCenterEnabled:             this.$arenaConfig.eventCenterEnabled,
							eventCenterShowAllEvents:       this.$arenaConfig.eventCenterShowAllEvents,
							videoCenterEnabled:             this.$arenaConfig.videoCenterEnabled,
							videoCenterLiveUpcomingEnabled: this.$arenaConfig.videoCenterLiveUpcomingEnabled,
						}),
						replays: resolveReplayEventsMoreLink({
							eventCenterBaseUrl:       this.eventCenterBaseUrl,
							videoCenterBaseUrl:       this.videoCenterBaseUrl,
							eventCenterEnabled:       this.$arenaConfig.eventCenterEnabled,
							eventCenterShowAllEvents: this.$arenaConfig.eventCenterShowAllEvents,
							videoCenterEnabled:       this.$arenaConfig.videoCenterEnabled,
						}),
					},
					showNavigationLane: this.teams.length > 0,
					momentsLaneEnabled: this.momentsLaneEnabled,
					momentsLaneItems:   this.momentsLaneItems,
				}
			);
		},
		headerSponsors() {
			const headerSponsors = this.ads.find(ad => ad.type === AdType.SPONSORS_HEADER);
			return headerSponsors ? headerSponsors.logos : null;
		},
	},
};
</script>

<style>

</style>
